import React, { Fragment, useState, useEffect, useRef } from 'react';
import 'aframe';
import '../primitives/hotspot';
import { useGetVideoThumb, usePrevious } from '../../../hooks';
import { customGaEvent, GA_EVENTS } from '../../../utils/ga';
import AframeIcon from '../components/AframeIcon';
import AframeTitle from '../components/AframeTitle';
import HotspotModal from '../components/HotspotModal/HotspotModal';

const VideoHotspot = ({
  active,
  showBaseHotspot,
  addAsset,
  assets = {},
  cameraPosition,
  darkMode,
  showLabel,
  rotation,
  position,
  hotspotId,
  isBeingEdited,
  isDisabled,
  title,
  onClick,
  handlePlay,
  playing,
  tourId,
  tourName,
  video = {},
  toggleMainMediaStatus,
  hidden,
  showHotspotModal,
  aid,
  hideGlow,
  iconSize,
  outWorldDisplay,
  icon,
  iconSrc,
}) => {
  const titleId = showLabel ? `title_${hotspotId}` : null;
  const iconId = `icon_${hotspotId}`;
  const contentId = `content_${hotspotId}`;
  const playButtonId = `play_${hotspotId}`;
  const rigId = `rig_${hotspotId}`;
  const customIconId = `custom-icon_${hotspotId}`;
  const customAssetId = `custom-asset_${hotspotId}`;
  const focusId = `focus-outline_${hotspotId}`;
  const useCustomIcon = icon && iconSrc;

  const [imageLoaded, setImageLoaded] = useState(false);
  const [centerHotspot, setCenterHotspot] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const iconButtonRef = useRef();
  const thumbnailRef = useRef();
  const videoIconRef = useRef();
  const thumb = useGetVideoThumb(video.url);

  const prevThumb = usePrevious(thumb);
  const prevMediaId = usePrevious(video.id);

  useEffect(() => {
    const thumbnail = thumbnailRef.current;
    const videoIcon = videoIconRef.current;
    const iconButton = iconButtonRef.current;

    const handleOpen = () => {
      onClick(hotspotId, isDisabled);
      setCenterHotspot(true);
      toggleMainMediaStatus(false);
    };

    const toggleModal = () => {
      setShowModal(!showModal);
      toggleMainMediaStatus(false);
    };

    const handleThumbnailClick = () => {
      if (thumbnail.getAttribute('visible')) {
        toggleModal();
        customGaEvent({
          action: GA_EVENTS.HOTSPOT_VIDEO_PLAY,
          label: title,
        });
      }
    };

    const handleIconClick = () => {
      // note - this pauses main media
      if (
        typeof handlePlay === 'function' &&
        playing &&
        thumbnail.getAttribute('visible')
      ) {
        handlePlay();
      }
    };

    if (prevThumb !== thumb) {
      setImageLoaded(false);
      if (typeof addAsset === 'function') {
        addAsset(video.id, `${thumb}`, '');
      }
    }

    if (assets[video.id] && assets[video.id].loaded) {
      const image = document.getElementById(video.id);
      image && setImageLoaded(true);
    }

    if (prevMediaId !== video.id && videoIcon) {
      const image = document.getElementById(video.id);
      image && setImageLoaded(true);
      image && videoIcon.setAttribute('src', `#${video.id}`);
    }
    iconButton?.addEventListener('click', handleOpen);

    imageLoaded && thumbnail?.addEventListener('click', handleThumbnailClick);

    videoIcon?.addEventListener('click', handleIconClick);

    return () => {
      iconButton && iconButton.removeEventListener('click', handleOpen);
      thumbnail && thumbnail.removeEventListener('click', handleThumbnailClick);
      videoIcon && videoIcon.removeEventListener('click', handleIconClick);
    };
  }, [
    aid,
    addAsset,
    assets,
    contentId,
    handlePlay,
    hotspotId,
    imageLoaded,
    isDisabled,
    onClick,
    playing,
    prevMediaId,
    prevThumb,
    thumb,
    thumbnailRef,
    title,
    toggleMainMediaStatus,
    tourId,
    tourName,
    video.alt,
    video.id,
    videoIconRef,
    showHotspotModal,
    showModal,
  ]);

  return (
    <Fragment>
      {/* Ignore imageLoaded for cypress test for now */}
      {((assets[video.id] && window.Cypress) ||
        (assets[video.id] && imageLoaded)) && (
        <a-entity id={rigId} rotation={rotation}>
          <a-hotspot
            active={active && showBaseHotspot}
            position={position}
            content={contentId}
            underedits={isBeingEdited}
            disabled={isDisabled}
            icon={useCustomIcon ? customIconId : iconId}
            title={titleId}
            id={`hotspot_${hotspotId}`}
            rotateto={cameraPosition}
            translatecontent="0 3.4 0.4"
            play={playButtonId}
            type="video"
            darkmode={darkMode}
            tourId={tourId}
            tourName={tourName}
            hotspotName={title}
            visible={!hidden}
            aid={aid}
            centerHotspot={centerHotspot}
            data-testid={`hotspot_${hotspotId}`}
            data-cy={`hotspot_${hotspotId}`}
            focusoutlineicon={focusId}
          >
            {!outWorldDisplay && (
              <a-image
                id={contentId}
                src={`#${video.id}`}
                width="8"
                height="5"
                position="0 3.4 -0.4"
                scale="1 1 1"
                visible="false"
                material="side: front; alpha-test: 0.5;"
                align="center"
                opacity="0.8"
                ref={thumbnailRef}
              >
                <a-image
                  width="1"
                  height="1"
                  class={active ? 'clickable' : ''}
                  position="0 0 0.1"
                  scale="1 1 1"
                  align="center"
                  opacity="0.8"
                  material="side: front; alpha-test: 0.5;"
                  src="#hotspot__play-icon-black"
                  id={playButtonId}
                  ref={videoIconRef}
                />
              </a-image>
            )}
            {showLabel && (
              <AframeTitle
                id={titleId}
                title={title}
                darkMode={darkMode}
                iconSize={iconSize}
              />
            )}
            <AframeIcon
              focusId={focusId}
              forwardRef={iconButtonRef}
              src={
                icon
                  ? `#${customAssetId}`
                  : `#hotspot__video-icon${darkMode ? '-black' : ''}`
              }
              id={icon ? customIconId : iconId}
              darkMode={darkMode}
              hideGlow={hideGlow}
              iconSize={iconSize}
              data-testid={icon ? customIconId : iconId}
            />
          </a-hotspot>
          {/* a custom icon that only applies to a single hotspot */}
          {useCustomIcon && (
            <a-asset>
              <img
                src={iconSrc}
                id={customAssetId}
                crossOrigin="anonymous"
                alt=""
              />
            </a-asset>
          )}
        </a-entity>
      )}
      <HotspotModal
        video={video}
        autoPlay={showModal && showBaseHotspot}
        name={title}
        hotspotName={title}
        closeModal={() => {
          onClick(hotspotId, isDisabled);
          setCenterHotspot(false);
          setShowModal(false);
        }}
        showModal={(active && !showBaseHotspot) || showModal}
        hotspotId={hotspotId}
        type={'videos'}
        src={video.src}
        isBeingEdited={isBeingEdited}
      />
    </Fragment>
  );
};

export default VideoHotspot;
